import { PreviewSvgModelInstance } from '../../../store/_models/previewSvg';
import React, { useEffect } from 'react';
import { View } from 'react-native';
import { MultiPreviewContent } from '@photosi/rn-components/preview-content/multi-preview-content/MultiPreviewContent';
import Datadog from '../../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import { useResponsiveScale } from '../../../contexts/ResponsiveScaleContext';
import { observer } from 'mobx-react-lite';
import { FramePreviewBackground } from './FramePreviewBackground';
import { FramePreviewSvg } from './FramePreviewSvg';
import { PreviewContent } from '@photosi/rn-components/preview-content/preview-content/PreviewContent';
import { productPreviewTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { styles } from './FramePreview.styles';
import Animated, {
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

type FramePreviewProps = {
  preview: PreviewSvgModelInstance;
  editable: boolean;
  showGrid: boolean;
  swappable: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  isHighlighted?: SharedValue<boolean> | boolean;
  showBadges?: boolean;
};

const FramePreview = observer(
  ({
    preview,
    disabled,
    editable,
    showGrid,
    swappable,
    disableSwapImageAnimation = false,
    isHighlighted = false,
    showBadges = true,
  }: FramePreviewProps) => {
    const { responsiveScaleSV, responsiveScale } = useResponsiveScale();

    useEffect(() => {
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.configuration_option_apply_time);
    }, [preview?.frameUrl, preview?.backgroundUrl]);

    const borderAnimatedStyle = useAnimatedStyle(() => {
      'worklet';

      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        return {
          opacity: isHighlighted.value ? 1 : 0,
        };
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        return {
          opacity: isHighlighted ? 1 : 0,
        };
      }
      return {
        opacity: 0,
      };
    });
    const transformScaleSV = useSharedValue(1);
    useDerivedValue(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        transformScaleSV.value = withTiming(isHighlighted.value ? 0.98 : 1, { duration: 250 });
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        transformScaleSV.value = withTiming(isHighlighted ? 0.98 : 1, { duration: 250 });
      }
    }, [isHighlighted]);

    const sizeAnimatedStyle = useAnimatedStyle(
      () => ({
        width: preview.viewBox.width * responsiveScaleSV.value,
        height: preview.viewBox.height * responsiveScaleSV.value,
        transform: [{ scale: transformScaleSV.value }],
      }),
      [responsiveScaleSV, preview.viewBox.width, preview.viewBox.height, transformScaleSV, responsiveScale],
    );

    return (
      // PREVIEW CONTAINER
      <View
        style={[styles.previewContainer]}
        testID={productPreviewTestID(preview.userImageArea.productId, TestIDs.FRAME)}
      >
        <Animated.View style={[sizeAnimatedStyle]}>
          {preview.backgroundUrl ? (
            <FramePreviewSvg viewBox={preview.viewBox} svgUrl={preview.backgroundUrl} zIndex={0} />
          ) : (
            <FramePreviewBackground viewBox={preview.innerViewBox} zIndex={0} />
          )}
          {preview.frameUrl && <FramePreviewSvg viewBox={preview.viewBox} svgUrl={preview.frameUrl} zIndex={2} />}

          {preview.isMultiphoto ? (
            <MultiPreviewContent
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              swappable={swappable}
              preview={preview}
              disableSwapImageAnimation={disableSwapImageAnimation}
              emptyStateWithText={false}
            />
          ) : (
            <PreviewContent
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              preview={preview}
              disableSwapImageAnimation={disableSwapImageAnimation}
              style={styles.previewContent}
              showBadges={showBadges}
              emptyStateWithText={true}
            />
          )}
        </Animated.View>
        {/* HIGHLIGHTED BORDER */}
        <Animated.View style={[styles.highlighted, sizeAnimatedStyle, borderAnimatedStyle]} />
      </View>
    );
  },
);

export default FramePreview;
